import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * CitedAnswer — renders a chatbot answer that contains inline grounding tokens
 * like "[tweet:<id>]", "[tv:<id>]", "[narrative:<id>]" and turns them into
 * Perplexity-style numbered superscript citations + a "Sources" footer.
 *
 * Tokens are emitted by the backend synthesizer (chatbot/grounding.py); the id is
 * the source row's uuid. We parse them from the answer TEXT (so this also works for
 * messages reloaded from history), then resolve each ref to a real, org-scoped
 * source record via `resolve` (POST /chatbot/citations/resolve). The resolver
 * returns a `nav` hint per source, so clicking a citation drills into the source.
 */

const TYPE_LABEL = {
  tweet: 'X / Twitter',
  tg: 'Telegram',
  tiktok: 'TikTok',
  ig: 'Instagram',
  tv: 'TV',
  fb: 'Facebook',
  news: 'News / Article',
  narrative: 'Narrative',
  signal: 'Signal',
  action: 'Action',
  campaign: 'Campaign',
  tracker: 'Tracker',
};

// Keep in sync with PREFIX_TABLE in chatbot/grounding.py. ASCII-only by design so
// tokens never corrupt RTL/Hebrew rendering.
const TOKEN_RE = /\[(tweet|tg|tiktok|ig|tv|fb|news|narrative|signal|action|campaign|tracker):([^\]\s]+)\]/g;

function parseAnswer(text) {
  const parts = [];
  const order = [];
  const numByKey = new Map();
  let last = 0;
  let m;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    const [token, type, id] = m;
    if (m.index > last) parts.push({ kind: 'text', value: text.slice(last, m.index) });
    const key = `${type}:${id}`;
    if (!numByKey.has(key)) {
      numByKey.set(key, numByKey.size + 1);
      order.push({ num: numByKey.size, type, id, token });
    }
    parts.push({ kind: 'cite', num: numByKey.get(key), type, id });
    last = m.index + token.length;
  }
  if (last < text.length) parts.push({ kind: 'text', value: text.slice(last) });
  return { parts, sources: order };
}

export default function CitedAnswer({ text, isRTL = false, showSources = true, resolve }) {
  const navigate = useNavigate();
  const { parts, sources } = useMemo(() => parseAnswer(text || ''), [text]);
  const [meta, setMeta] = useState({});

  const sourcesKey = sources.map((s) => `${s.type}:${s.id}`).join(',');

  useEffect(() => {
    let alive = true;
    if (!resolve || sources.length === 0) return undefined;
    Promise.resolve(resolve(sources.map((s) => ({ type: s.type, id: s.id }))))
      .then((list) => {
        if (!alive) return;
        const m = {};
        (list || []).forEach((r) => { m[`${r.type}:${r.id}`] = r; });
        setMeta(m);
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesKey, resolve]);

  const applyNav = (nav) => {
    if (!nav) return;
    if (nav.video_url) window.open(nav.video_url, '_blank', 'noopener');
    else if (nav.route) navigate(nav.route, nav.state ? { state: nav.state } : undefined);
    else if (nav.url) window.open(nav.url, '_blank', 'noopener');
  };

  const recFor = (type, id) => meta[`${type}:${id}`];
  const navFor = (type, id) => recFor(type, id)?.nav;
  const tipFor = (type, id) => {
    const r = recFor(type, id);
    const head = TYPE_LABEL[type] || type;
    const body = r?.title || r?.snippet;
    return body ? `${head} — ${body}` : `${head} · ${id}`;
  };

  return (
    <>
      {parts.map((p, i) =>
        p.kind === 'text' ? (
          <React.Fragment key={i}>{p.value}</React.Fragment>
        ) : (
          <sup
            key={i}
            title={tipFor(p.type, p.id)}
            onClick={navFor(p.type, p.id) ? () => applyNav(navFor(p.type, p.id)) : undefined}
            style={{
              display: 'inline-block',
              minWidth: 16,
              padding: '0 4px',
              margin: isRTL ? '0 1px 0 2px' : '0 2px 0 1px',
              borderRadius: 6,
              background: '#e0f2fe',
              color: '#0369a1',
              fontSize: 10,
              fontWeight: 600,
              lineHeight: '15px',
              textAlign: 'center',
              cursor: navFor(p.type, p.id) ? 'pointer' : 'default',
              verticalAlign: 'super',
              direction: 'ltr',
              unicodeBidi: 'isolate',
            }}
          >
            {p.num}
          </sup>
        )
      )}

      {showSources && sources.length > 0 && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          <span style={{ fontSize: 11, color: '#9ca3af' }}>Sources</span>
          {sources.map((s) => {
            const r = recFor(s.type, s.id);
            const nav = r?.nav;
            const label = r?.title || r?.snippet || `${TYPE_LABEL[s.type] || s.type} · ${s.id.slice(0, 8)}`;
            return (
              <div
                key={s.num}
                onClick={nav ? () => applyNav(nav) : undefined}
                title={r?.snippet || ''}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  fontSize: 12,
                  color: '#4b5563',
                  cursor: nav ? 'pointer' : 'default',
                  padding: '3px 6px',
                  borderRadius: 8,
                  background: '#f9fafb',
                  border: '1px solid #eef0f2',
                }}
              >
                <span style={{ color: '#0369a1', fontWeight: 600, minWidth: 14 }}>{s.num}</span>
                <span style={{ color: '#9ca3af', flexShrink: 0 }}>{TYPE_LABEL[s.type] || s.type}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
